import { useEffect, useId, useMemo, useState } from "react";
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  AutoCompleteCompleteEvent,
  AutoCompleteProps,
} from "primereact/autocomplete";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Checkbox } from "primereact/checkbox";
import { VirtualScroller } from "primereact/virtualscroller";
import { useTimeout } from "primereact/hooks";

import { Result } from "@/components";
import { CloseIcon, SearchIcon, SpinnerIcon } from "@/components/atoms/icons";
import { cn } from "@/styles/cn";
import { getValueKey } from "@/components/molecules/SelectBase";

import helpers from "./helpers/helpers";
import { CustomValidationLogic, Option } from "./models/MultiAutocomplete.types";
import { minLengthSimpleValidation } from "./helpers/minLengthSimpleValidation";
import { useHistory } from "./hook/useHistory";

export type MultiAutocompleteInlineProps<T extends Option> = Omit<AutoCompleteProps, "onChange"> & {
  listClassName?: string;
  checkboxClassName?: string;
  dataKey: string;
  value?: T[];
  onChange?: (value: T[]) => void;
  placeholderAutocomplete?: string;
  historyLength?: number;
  historyStorayKey?: string;
  historyFilter?: (item: T[]) => T[];
  loading?: boolean;

  /**
   * Permite customizar la logica de validacion previa antes de enviar consulta al backend
   * Por defecto valida que el valor ingresado tenga al menos minLength caracteres
   *
   * @param value nuevo valor ingresado por el usuario
   * @returns string mensaje de error a mostrar al usuario, sii es undefined, procede con la búqueda
   */
  validationCustomLogic?: CustomValidationLogic;
};

export function MultiAutocompleteInline<T extends Option>({
  className,
  listClassName,
  checkboxClassName,
  field = "label",
  dataKey,
  value: valueProp,
  onChange,
  suggestions,
  completeMethod,
  delay = 500,
  minLength = 3,
  validationCustomLogic = minLengthSimpleValidation,
  forceSelection = false,
  showEmptyMessage = true,
  emptyMessage = "No se encontraron resultados",
  placeholder,
  placeholderAutocomplete = placeholder,
  historyLength = 10,
  historyStorayKey,
  historyFilter,
  itemTemplate,
  loading,
  ...props
}: MultiAutocompleteInlineProps<T>) {
  const inputId = useId();
  const [key, setKey] = useState(0); // to avoid an unwanted loading feedback on the Autocomplete
  const [valueAutocomplete, setValueAutocomplete] = useState<string>("");
  const [selected, setSelected] = useState<T[]>([]);
  const [isDirtySuggestions, setIsDirtySuggestions] = useState(false);
  const [history, addHistory] = useHistory<T>({
    length: historyLength,
    dataKey,
    storageKey: historyStorayKey,
  });
  const [error, setError] = useState("");

  const hasValue = valueAutocomplete !== "";

  const isEmptyResult =
    hasValue &&
    !loading &&
    !isDirtySuggestions &&
    valueAutocomplete.length >= minLength &&
    suggestions?.length === 0;

  const msgError = validationCustomLogic(valueAutocomplete, { minLength: minLength });

  const computedError = useMemo(() => {
    if (error) {
      return error;
    }

    return isEmptyResult && showEmptyMessage ? emptyMessage : undefined;
  }, [error, isEmptyResult, showEmptyMessage, emptyMessage]);

  useEffect(() => {
    setIsDirtySuggestions(false);
  }, [suggestions]);

  useTimeout(
    () => {
      setError(msgError!);
    },
    delay,
    typeof msgError !== "undefined",
  );

  useEffect(() => {
    setSelected(valueProp ?? []);
  }, [valueProp]);

  const items = useMemo(() => {
    let items: (T | { isDividerHistory: boolean })[] = [...(selected ?? [])];
    const historyOnly = helpers.diff(history, selected);

    const historyFiltered = historyFilter ? historyFilter(historyOnly) : historyOnly;

    if (historyFiltered.length > 0) {
      items = [...items, { isDividerHistory: true }, ...historyFiltered];
    }

    return helpers.removeDuplicates(items, dataKey);
  }, [selected, history, historyFilter, dataKey]);

  const handleOnToggleMultiple = (value: T, checked: boolean) => {
    const newSelected = (selected ?? []).filter((itemValue: T) => itemValue !== value);
    if (checked) {
      newSelected.push(value);
    }
    setSelected(newSelected);
    onChange?.(newSelected);
  };

  const checkList = (opt: T | { isDividerHistory: boolean }) => {
    if ("isDividerHistory" in opt) {
      return (
        <li
          key="isDividerHistory"
          className="mb-1 mt-2 flex items-center text-sm italic text-urbe-textSecondary"
        >
          Búsquedas recientes
        </li>
      );
    }

    const label = opt[field] as string;
    const valueKey = getValueKey(opt, { optionLabel: field, dataKey });
    const checked = helpers.isInclude(valueProp, opt, dataKey);
    const inputId = `checkList-${valueKey}`;
    return (
      <li key={valueKey} className="flex">
        <Checkbox
          checked={checked}
          className={cn(
            "self-center",
            { "self-start pt-[0.5rem]": !!itemTemplate },
            checkboxClassName,
          )}
          inputId={inputId}
          onChange={(e) => handleOnToggleMultiple(opt, e.checked === true)}
        />
        <label
          className={cn(
            "w-full cursor-pointer px-2 py-1 text-[0.75rem] font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
            "",
          )}
          htmlFor={inputId}
        >
          {itemTemplate && typeof itemTemplate === "function" && itemTemplate(opt, -1)}
          {itemTemplate && typeof itemTemplate !== "function" && itemTemplate}
          {!itemTemplate && label}
        </label>
      </li>
    );
  };

  const handleOnAutocompleteChange = (e: AutoCompleteChangeEvent) => {
    const newValue = e.value;

    if (!newValue) {
      setValueAutocomplete("");
      setIsDirtySuggestions(true);
      return;
    }

    if (typeof newValue === "string") {
      setValueAutocomplete(newValue);
      setIsDirtySuggestions(true);
      return;
    }

    setError("");
    setValueAutocomplete("");
    const newSelected = helpers.removeDuplicates([...(selected ?? []), newValue], dataKey);
    addHistory(newValue);
    setSelected(newSelected);
    onChange?.(newSelected);
  };

  const clearValue = () => {
    setError("");
    setValueAutocomplete("");
  };

  const handleCompleteMethod = (e: AutoCompleteCompleteEvent) => {
    const msgError = validationCustomLogic(e.query, { minLength });
    if (msgError) {
      setKey(key + 1); // to avoid an unwanted loading feedback on the Autocomplete
      return;
    }
    setError("");
    completeMethod?.(e);
  };

  return (
    <div className="flex h-full min-h-1 w-full flex-col gap-1">
      <label className="text-sm" htmlFor={inputId}>
        {placeholderAutocomplete}:
      </label>
      <IconField className="relative w-full" iconPosition="right">
        <InputIcon className="right-4">
          {loading && (
            <div className="flex w-[50px] justify-end bg-white">
              <SpinnerIcon className="animate-spin" color="#999" size={18} />
            </div>
          )}

          {!loading && !hasValue && <SearchIcon color="#2B2B36" size={20} />}
          {!loading && hasValue && (
            <CloseIcon
              className="cursor-pointer fill-urbix-gray-800 hover:fill-primary-400"
              size={20}
              onClick={clearValue}
            />
          )}
        </InputIcon>
        <AutoComplete
          key={key}
          {...props}
          autoFocus
          completeMethod={handleCompleteMethod}
          field={field}
          forceSelection={forceSelection}
          inputId={inputId}
          invalid={!!computedError}
          itemTemplate={itemTemplate}
          minLength={minLength}
          pt={{
            root: [cn("w-full placeholder:not-italic", className)],
            input: {
              root: [
                "w-full h-[40px]",
                "text-sm text-gray-700 bg-white py-1 px-3 border border-urbe-border transition duration-200 rounded-sm appearance-none",
                "hover:border-gray-300 focus:outline-none focus:outline-offset-0 focus:border-gray-300",
                {
                  "border-error text-urbe-error": isEmptyResult,
                },
              ],
            },
            loadingIcon: ["bg-white right-[2.5rem] text-urbe-border z-auto"],
          }}
          showEmptyMessage={false}
          suggestions={suggestions}
          value={valueAutocomplete}
          onChange={handleOnAutocompleteChange}
        />
      </IconField>

      {isEmptyResult && <Result.Empty variant="textInline" />}

      {!isEmptyResult && (
        <div className="relative flex h-full min-h-1 w-full flex-col">
          {computedError && <span className="text-sm text-urbe-error">{computedError}</span>}
          <ul
            className={cn(
              "relative flex h-full w-full flex-col gap-2 overflow-hidden",
              listClassName,
            )}
          >
            <VirtualScroller
              autoSize
              className="border-1 surface-border border-round absolute inset-0 h-full min-h-[100px] w-full flex-1 overflow-auto p-2"
              itemSize={25}
              itemTemplate={checkList}
              items={items}
            />
          </ul>
        </div>
      )}
    </div>
  );
}
